import { logger } from '@/core/logger';
import { RegistrationStorage } from '@/modules/onboarding/registration/storage/registrationStorage';
import { usePasscode } from '@/modules/passcode';
import { RegistrationState, WelcomeScreen } from '@/modules/welcome';
import { useFocusEffect } from '@react-navigation/native';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { BackHandler, Platform } from 'react-native';

import { ENABLE_UAEPASS } from '@/core/constants/appConfig';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';

export default function Welcome() {
    const { state } = useLocalSearchParams<{ state?: string }>();
    const pathname = usePathname();
    const { checkPasscodeState } = usePasscode();
    const [currentState, setCurrentState] = useState<RegistrationState>(
        (state as RegistrationState) || RegistrationState.INITIAL
    );

    // Update state when URL parameter changes
    useEffect(() => {
        if (state && Object.values(RegistrationState).includes(state as RegistrationState)) {
            setCurrentState(state as RegistrationState);
        } else {
            setCurrentState(RegistrationState.INITIAL);
        }
    }, [state]);

    const updateURL = useCallback((newState: RegistrationState) => {
        // Only update URL on web platform to prevent mobile animation issues
        if (Platform.OS === 'web') {
            if (newState === RegistrationState.INITIAL) {
                router.replace('/welcome');
            } else {
                router.replace(`/welcome?state=${newState}`);
            }
        }
    }, []);

    // Handle hardware back button
    const handleBackPress = useCallback(() => {
        let newState: RegistrationState;
        switch (currentState) {
            case RegistrationState.UAE_METHODS:
                newState = RegistrationState.UAE_OPTIONS;
                break;
            case RegistrationState.UAE_OPTIONS:
            case RegistrationState.INTERNATIONAL:
            case RegistrationState.LOGIN:
                newState = RegistrationState.INITIAL;
                break;
            default:
                return;
        }
        setCurrentState(newState);
        updateURL(newState);
    }, [currentState, updateURL]);

    useFocusEffect(
        useCallback(() => {
            const backAction = () => {
                if (currentState !== RegistrationState.INITIAL) {
                    handleBackPress();
                    return true; // Prevent default behavior (exit app)
                }
                return false; // Allow default behavior (exit app)
            };

            const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction);
            return () => backHandler.remove();
        }, [currentState, handleBackPress])
    );

    const handleRegister = () => {
        if (!ENABLE_UAEPASS) {
            // UAE Pass disabled: directly show UAE options (UAE Citizen/International)
            const newState = RegistrationState.UAE_OPTIONS;
            setCurrentState(newState);
            updateURL(newState);
        } else {
            // UAE Pass enabled: show intermediate screen
            const newState = RegistrationState.UAE_OPTIONS;
            setCurrentState(newState);
            updateURL(newState);
        }
    };

    const handleUAECitizenPress = () => {
        if (!ENABLE_UAEPASS) {
            // UAE Pass disabled: directly navigate to manual registration form with EID flow
            logger.info('Welcome:UAE Pass disabled, navigating directly to EID registration');
            router.push('/registration-form?type=emirates-id');
        } else {
            // UAE Pass enabled: show UAE methods screen (UAE Pass or Emirates ID)
            const newState = RegistrationState.UAE_METHODS;
            setCurrentState(newState);
            updateURL(newState);
        }
    };

    const handleInternationalPress = () => {
        if (!ENABLE_UAEPASS) {
            // UAE Pass disabled: directly navigate to manual registration form with passport flow
            logger.info('Welcome:UAE Pass disabled, navigating directly to passport registration');
            router.push('/registration-form?type=passport');
        } else {
            // UAE Pass enabled: show international methods screen
            const newState = RegistrationState.INTERNATIONAL;
            setCurrentState(newState);
            updateURL(newState);
        }
    };

    const handleLogin = async () => {
        trackCatalogEvent({
            screen: DT_SCREEN.WELCOME,
            actionName: 'welcome_login_tap',
            eventType: 'action',
        });
        if (!ENABLE_UAEPASS) {
            // UAE Pass disabled: directly navigate to manual login
            logger.info('Welcome:UAE Pass disabled, navigating directly to login');
            router.push('/login');
            return;
        }

        // UAE Pass enabled: check if passcode is set
        const passcodeSet = await checkPasscodeState();
        if (passcodeSet) {
            // If passcode is set, navigate directly to PasscodeLoginScreen
            logger.info('Welcome:Passcode set, navigating to passcode-login');
            router.push('/passcode-login' as any);
        } else {
            // No passcode, show login options
            const newState = RegistrationState.LOGIN;
            setCurrentState(newState);
            updateURL(newState);
        }
    };

    const handleLoginWithEmail = () => {
        // Navigate to login screen
        router.push('/login');
    };

    const handleLoginWithUAEPass = async () => {
        // Check if user has reached registration success
        const hasReachedSuccess = await RegistrationStorage.hasReachedRegistrationSuccess();
        if (hasReachedSuccess) {
            // Redirect to registration success screen
            router.replace('/registration-success');
        } else {
            // Navigate to UAE Pass authentication flow
            router.push('/uaepass');
        }
    };

    const handleLanguageSwitch = () => {
        // Handle language switching logic
        logger.info('Welcome', 'Language switch pressed');
    };

    const handleUAEPassPress = async () => {
        // Check if user has reached registration success
        const hasReachedSuccess = await RegistrationStorage.hasReachedRegistrationSuccess();
        if (hasReachedSuccess) {
            // Redirect to registration success screen
            logger.info('Welcome', 'User has reached registration success, redirecting');
            router.replace('/registration-success');
            return;
        }

        // Singleton behavior: Check if uaepass is already the current route
        if (pathname === '/uaepass') {
            logger.info('Welcome', 'UAE Pass auth screen already active; preventing duplicate navigation');
            return;
        }

        logger.info('Welcome', 'Navigating to UAE Pass auth screen from', pathname);
        router.push('/uaepass');
    };

    const handleEmiratesIdPress = () => {
        router.push('/registration-form?type=emirates-id');
    };

    const handlePassportPress = () => {
        router.push('/registration-form?type=passport');
    };

    return (
        <WelcomeScreen
            currentState={currentState}
            onRegisterPress={handleRegister}
            onLoginPress={handleLogin}
            onLoginWithEmailPress={handleLoginWithEmail}
            onLoginWithUAEPassPress={handleLoginWithUAEPass}
            onLanguagePress={handleLanguageSwitch}
            onUAECitizenPress={handleUAECitizenPress}
            onInternationalPress={handleInternationalPress}
            onUAEPassPress={handleUAEPassPress}
            onEmiratesIdPress={handleEmiratesIdPress}
            onPassportPress={handlePassportPress}
            onBackPress={handleBackPress}
        />
    );
}
